import {
  convert,
  DayOfWeek,
  LocalDate,
  LocalDateTime,
  TemporalAdjusters,
  ZonedDateTime,
  ZoneId,
} from "@js-joda/core";
import { useState } from "react";
import DropOffLocation from "@modules/drop-off-location/types/DropOffLocation";
import { Trans, useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { formatTimeOnly } from "@talladega/helpers";
import WeeklyAppointmentTimePicker from "@components/WeeklyAppointmentTimePicker";
import {
  Button,
  LoadingSpinner,
  Modal,
  OverlayLoader,
  toastPop,
} from "@talladega/components";
import useUnavailableHoursForPeriod from "@modules/drop-off-location/hooks/getUnavailableHoursForWeek";
import "@js-joda/timezone/dist/js-joda-timezone-10-year-range";

export type OnAppointmentConfirmed = (
  {
    appointmentAt,
    dropOffLocationId,
  }: { appointmentAt: LocalDateTime; dropOffLocationId: string },
  { onSuccess, onError }: { onSuccess: () => void; onError: () => void }
) => void;

type Props = {
  vehicle: {
    year: number | null;
    make: string | null;
    model: string | null;
  };
  dropOffAppointmentAt?: string | null;
  dropOffLocation: DropOffLocation | undefined;
  onAppointmentConfirmed: OnAppointmentConfirmed;
  showLoader?: boolean;
};

const PickDropOffAppointmentDate = ({
  dropOffLocation,
  dropOffAppointmentAt,
  vehicle,
  onAppointmentConfirmed,
  showLoader,
}: Props) => {
  const { t } = useTranslation(["common", "purchase"]);
  const { locale } = useRouter();
  const [selectedAppointmentDateTime, setSelectedAppointmentDateTime] =
    useState<LocalDateTime | null>(null);
  const [
    isAppointmentConfirmationModalOpen,
    setIsAppointmentConfirmationModalOpen,
  ] = useState<boolean>(false);

  const onConfirmDateAndTime = () => {
    if (selectedAppointmentDateTime && dropOffLocation) {
      onAppointmentConfirmed(
        {
          appointmentAt: selectedAppointmentDateTime,
          dropOffLocationId: dropOffLocation.id,
        },
        {
          onSuccess: () => {
            setIsAppointmentConfirmationModalOpen(false);
          },
          onError: () => {
            toastPop(
              "warning",
              t("purchase:appointment_unavailable"),
              t("purchase:sorry_appointment_unavailable")
            );
            setIsAppointmentConfirmationModalOpen(false);
          },
        }
      );
    }
  };

  const onAppointmentDateTimeSelected = (
    appointmentDateTime: LocalDateTime | null
  ) => {
    setSelectedAppointmentDateTime(appointmentDateTime);
  };

  const [showWeekOf, setShowWeekOf] = useState(
    LocalDate.now().with(TemporalAdjusters.previousOrSame(DayOfWeek.SUNDAY))
  );

  const { unavailableSlots } = useUnavailableHoursForPeriod(
    dropOffLocation?.id,
    LocalDate.now().plusDays(1),
    LocalDate.now()
      .plusWeeks(2)
      .with(TemporalAdjusters.nextOrSame(DayOfWeek.SATURDAY))
  );

  const handleWeekBack = () => {
    const previousWeek = showWeekOf.with(
      TemporalAdjusters.previous(DayOfWeek.SUNDAY)
    );

    // don't let them go earlier than "today", or the week containing "today"
    if (
      !previousWeek.isBefore(
        LocalDate.now().with(TemporalAdjusters.previousOrSame(DayOfWeek.SUNDAY))
      )
    ) {
      setShowWeekOf(previousWeek);
    }
  };

  const handleWeekForward = () => {
    // still wanna make sure they can't go too far ahead
    const maxDate = LocalDate.now()
      .plusWeeks(2)
      .with(TemporalAdjusters.nextOrSame(DayOfWeek.SATURDAY));
    const nextWeek = showWeekOf.with(TemporalAdjusters.next(DayOfWeek.SUNDAY));

    if (maxDate.isAfter(nextWeek)) {
      setShowWeekOf(nextWeek);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-lg font-normal text-secondary">
        {t("purchase:please_select_a_drop_off_date_message")}
      </p>
      <LoadingSpinner isLoading={showLoader ?? false} isOverlay />
      {!showLoader &&
        dropOffLocation?.regularHours &&
        dropOffLocation?.specialHours && (
          <div className="px-4 py-6 shadow-elevation-01 rounded-lg">
            <WeeklyAppointmentTimePicker
              regularHours={dropOffLocation.regularHours}
              specialHours={dropOffLocation.specialHours}
              onAppointmentDateTimeSelected={onAppointmentDateTimeSelected}
              minAppointmentDate={ZonedDateTime.now(
                ZoneId.of(dropOffLocation.timeZone)
              )
                .toLocalDate()
                .plusDays(1)}
              weekOfDate={showWeekOf}
              existingAppointmentDateTime={
                dropOffAppointmentAt
                  ? LocalDateTime.parse(dropOffAppointmentAt)
                  : null
              }
              onWeekBack={handleWeekBack}
              onWeekForward={handleWeekForward}
              unavailableTimeSlots={unavailableSlots}
              timeZone={dropOffLocation.timeZone}
            />
          </div>
        )}

      <div>
        <Button
          size="large"
          className="flex justify-center items-center"
          buttonStyle="primary"
          fill="outline"
          disabled={!selectedAppointmentDateTime}
          onClick={() => setIsAppointmentConfirmationModalOpen(true)}
        >
          <p>{t("common:confirm_date_and_time")}</p>
        </Button>
      </div>
      <Modal
        title={t("common:summary")}
        isOpen={isAppointmentConfirmationModalOpen}
        modalType="none"
        onClose={() => setIsAppointmentConfirmationModalOpen(false)}
        alignment="center"
        disableClosing={showLoader}
      >
        <OverlayLoader isLoading={showLoader ?? false}>
          <>
            <h5>{t("purchase:ready_to_book_an_appointment")}</h5>
            <Trans
              t={t}
              i18nKey="purchase:appointment_confirmation_message"
              values={{
                vehicleYear: vehicle?.year,
                vehicleMake: vehicle?.make,
                vehicleModel: vehicle?.model,
                locationName: dropOffLocation?.name,
                appointmentDate: selectedAppointmentDateTime
                  ? convert(selectedAppointmentDateTime)
                      .toDate()
                      .toLocaleString(locale, {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                  : " ",
                appointmentTime: selectedAppointmentDateTime
                  ? formatTimeOnly(
                      convert(selectedAppointmentDateTime).toDate(),
                      `${locale === "fr" ? "fr" : "en"}-CA`
                    )
                  : " ",
              }}
              components={{
                b: <span className="font-semibold" />,
              }}
            />
          </>
          <Modal.Buttons
            primaryButtonText={t("purchase:lets_do_it")}
            secondaryButtonText={t("common:cancel")}
            onPrimaryClick={onConfirmDateAndTime}
            onSecondaryClick={() =>
              setIsAppointmentConfirmationModalOpen(false)
            }
          />
        </OverlayLoader>
      </Modal>
    </div>
  );
};

export default PickDropOffAppointmentDate;
